"use client";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { Link } from "@chakra-ui/next-js";
import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";

import { useRouter } from "next/router";
import { useCallback } from "react";
import { FaBars, FaChevronDown, FaChevronRight, FaTimes } from "react-icons/fa";
import { LanguageToggle } from "../LanguageToggle";
import defaultLogo from "@root/public/images/logos/clarifayo.png";
import Image from "next/image";

export default function Navbar({
  activeLinkColor,
  logo,
}: {
  languageToggleBgColor?: string;
  languageToggleTextColor?: string;
  signUpBgColor?: string;
  signUpTextColor?: string;
  logo?: string;
  activeLinkColor?: string;
}) {
  const { status } = useAuthenticatedUser();
  const { isOpen, onToggle } = useDisclosure();
  const { locale } = useRouter();
  const t = useTranslations();

  return (
    <Box
      top={0}
      zIndex={1}
      bgColor="transparent"
      px={{
        base: 4,
        md: 16,
      }}
      py={{
        base: 4,
        md: 8,
      }}
    >
      <Flex
        color={useColorModeValue("gray.600", "white")}
        minH={"60px"}
        py={{ base: 2 }}
        px={{ base: 4 }}
        align={"center"}
      >
        <Flex
          flex={{ base: undefined, md: "auto" }}
          ml={{ base: -2 }}
          display={{ base: "flex", md: "none" }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              isOpen ? (
                <FaTimes width={3} height={3} />
              ) : (
                <FaBars width={5} height={5} />
              )
            }
            variant={"primary"}
            aria-label={"Toggle Navigation"}
          />
        </Flex>
        <Flex
          flex={{ base: 1 }}
          justify={{ base: "center", md: "space-between" }}
          align="center"
        >
          <Link href={`/${locale}`}>
            <Image
              src={logo || defaultLogo}
              width={180}
              height={120}
              alt="logo"
            />
          </Link>

          <Flex display={{ base: "none", md: "flex" }} mr={4}>
            <DesktopNav activeLinkColor={activeLinkColor} />
          </Flex>
        </Flex>

        <Stack
          flex={{ base: 0, md: 0 }}
          gap={4}
          justify={"flex-end"}
          direction={"row"}
        >
          <Button
            as={"a"}
            display={{ base: "none", md: "inline-flex" }}
            variant="primary"
            href={"/auth/sign-up"}
          >
            {t("navbar.getStarted")}
          </Button>
          <Box
            display={{
              base: "none",
              md: "block",
            }}
            w="80px"
          >
            <LanguageToggle size="md" />
          </Box>
        </Stack>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav />
      </Collapse>
    </Box>
  );
}

const isPathEmpty = (path: string, locale: string) =>
  path === "/" || path === "/" + locale;

const isExcludedPath = (path: string, locale: string) =>
  ["/contact", "/enterprise"].includes(path) ||
  [`/${locale}/contact`, `/${locale}/enterprise`].includes(path);

const DesktopNav = ({
  activeLinkColor = "brand.900",
}: {
  activeLinkColor?: string;
}) => {
  const { status } = useAuthenticatedUser();
  const { locale, asPath } = useRouter();
  const linkColor = "black.50";
  const linkHoverColor = "brand.1000";
  const popoverContentBgColor = useColorModeValue("white", "gray.800");
  const t = useTranslations();

  const isLinkActive = useCallback(
    (href: string) => {
      if (isExcludedPath(asPath, locale || "")) return true;
      return asPath.includes(href);
    },
    [asPath, locale]
  );

  return (
    <Stack direction={"row"} spacing={6}>
      {NAV_ITEMS.map((navItem) => (
        <Box key={navItem.label}>
          <Popover trigger={"hover"} placement={"bottom-start"}>
            <PopoverTrigger>
              <Box
                as="a"
                p={2}
                href={"/" + locale + (navItem.href ?? "#")}
                fontFamily={"Gotham"}
                fontWeight={
                  isLinkActive(navItem.href || "/home") ? 500 : "normal"
                }
                color={
                  isLinkActive(navItem.href || "") ? activeLinkColor : linkColor
                }
                textTransform={"uppercase"}
                _hover={{
                  textDecoration: "none",
                  color: linkHoverColor,
                }}
              >
                <Text
                  fontSize={14}
                  position="relative"
                  /* _before={{
                    content: '""',
                    borderBottom: `${
                      isLinkActive(navItem.href || "") &&
                      !isPathEmpty(asPath, locale || "")
                        ? "3px solid"
                        : "0"
                    }`,
                    borderColor: "brand.100",
                    position: "absolute",
                    bottom: "-4px",
                    left: "calc(50% - 5.5px)",
                    right: 0,
                    borderRadius: "full",
                    width: "11px",
                  }} */
                >
                  {t(navItem.label)}
                </Text>
              </Box>
            </PopoverTrigger>

            {navItem.children && (
              <PopoverContent
                border={0}
                boxShadow={"xl"}
                bg={popoverContentBgColor}
                p={4}
                rounded={"xl"}
                minW={"sm"}
              >
                <Stack>
                  {navItem.children.map((child) => (
                    <DesktopSubNav key={child.label} {...child} />
                  ))}
                </Stack>
              </PopoverContent>
            )}
          </Popover>
        </Box>
      ))}
      {status === "authenticated" ? (
        <Box>
          <Box
            as="a"
            p={2}
            href="/router"
            fontFamily={"Gotham"}
            fontWeight={400}
            color={linkColor}
            textTransform={"uppercase"}
            _hover={{
              textDecoration: "none",
              color: linkHoverColor,
            }}
          >
            <Text fontSize={14} position="relative">
              {t("navbar.login")}
            </Text>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box
            as="a"
            p={2}
            href="/auth/sign-in"
            fontFamily={"Gotham"}
            fontWeight={400}
            color={linkColor}
            textTransform={"uppercase"}
            _hover={{
              textDecoration: "none",
              color: linkHoverColor,
            }}
            onClick={(e) => {
              e.preventDefault();
              signIn("auth0", {
                callbackUrl: `${window.location.origin}/router`,
              });
            }}
          >
            <Text fontSize={14} position="relative">
              {t("navbar.login")}
            </Text>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

const DesktopSubNav = ({ label, href, subLabel }: NavItem) => {
  const t = useTranslations();
  return (
    <Box
      as="a"
      href={href}
      role={"group"}
      display={"block"}
      p={2}
      rounded={"md"}
      _hover={{ bg: useColorModeValue("pink.50", "gray.900") }}
    >
      <Stack direction={"row"} align={"center"}>
        <Box>
          <Text
            transition={"all .3s ease"}
            _groupHover={{ color: "pink.400" }}
            fontWeight={500}
          >
            {t(label)}
          </Text>
          <Text fontSize={"sm"}>{subLabel}</Text>
        </Box>
        <Flex
          transition={"all .3s ease"}
          transform={"translateX(-10px)"}
          opacity={0}
          _groupHover={{ opacity: "100%", transform: "translateX(0)" }}
          justify={"flex-end"}
          align={"center"}
          flex={1}
        >
          <Icon color={"pink.400"} w={5} h={5} as={FaChevronRight} />
        </Flex>
      </Stack>
    </Box>
  );
};

const MobileNav = () => {
  const t = useTranslations();
  const { status } = useAuthenticatedUser();
  return (
    <Stack
      bg={useColorModeValue("white", "gray.800")}
      p={4}
      rounded="xl"
      position="relative"
      display={{ md: "none" }}
      zIndex={"docked"}
    >
      {NAV_ITEMS.map((navItem) => (
        <MobileNavItem key={navItem.label} {...navItem} />
      ))}
      <LanguageToggle size="md" w="full" showLabel />
      {status === "authenticated" ? (
        <Button
          mt={4}
          as={"a"}
          fontSize={"sm"}
          fontWeight={400}
          variant={"outline"}
          w="full"
          href="/router"
        >
          {t("navbar.login")}
        </Button>
      ) : (
        <Button
          as={"a"}
          mt={4}
          fontSize={"sm"}
          fontWeight={400}
          variant={"outline"}
          href="/auth/sign-in"
          w="full"
          onClick={(e) => {
            e.preventDefault();
            signIn("auth0", {
              callbackUrl: `${window.location.origin}/router`,
            });
          }}
        >
          {t("navbar.login")}
        </Button>
      )}
      <Button
        mt={4}
        as={"a"}
        fontSize={"sm"}
        fontWeight={600}
        variant="primary"
        href={"/auth/sign-up"}
        w="full"
      >
        {t("navbar.getStarted")}
      </Button>
    </Stack>
  );
};

const MobileNavItem = ({ label, children, href }: NavItem) => {
  const { isOpen, onToggle } = useDisclosure();
  const t = useTranslations();

  return (
    <Stack spacing={4} onClick={children && onToggle}>
      <Box
        py={2}
        as="a"
        href={href ?? "#"}
        justifyContent="space-between"
        alignItems="center"
        _hover={{
          textDecoration: "none",
        }}
      >
        <Text
          fontWeight={600}
          color={useColorModeValue("gray.600", "gray.200")}
        >
          {t(label)}
        </Text>
        {children && (
          <Icon
            as={FaChevronDown}
            transition={"all .25s ease-in-out"}
            transform={isOpen ? "rotate(180deg)" : ""}
            w={6}
            h={6}
          />
        )}
      </Box>

      <Collapse in={isOpen} animateOpacity style={{ marginTop: "0!important" }}>
        <Stack mt={2} pl={4} align={"start"}>
          {children &&
            children.map((child) => (
              <Box as="a" key={child.label} py={2} href={child.href}>
                {t(child.label)}
              </Box>
            ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

interface NavItem {
  label: string;
  subLabel?: string;
  children?: Array<NavItem>;
  href?: string;
}

const NAV_ITEMS: Array<NavItem> = [
  {
    label: "landingPage.toWhom",
    href: "/to-whom",
  },
  {
    label: "pricing",
    href: "/pricing",
  },
  {
    label: "learning",
    href: "/learning",
  },

  {
    label: "coachCommunity",
    href: "/coach-community",
  },
];
